import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type UserRole = 'admin' | 'user';

export interface SessionUser {
  email: string;
  name: string | null;
  picture: string | null;
  role: UserRole;
  /** True si l'utilisateur figure dans la liste des valideurs des paiements (Paiements > Paramètres). */
  is_payment_validator?: boolean;
}

interface UserContextValue {
  /** undefined = pas encore résolu ; null = non connecté ; objet = connecté. */
  user: SessionUser | null | undefined;
  /** Force un re-fetch de /auth/me (utile après login côté backend). */
  refresh: () => Promise<void>;
  logout: () => void;
}

const UserContext = createContext<UserContextValue | null>(null);

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null | undefined>(undefined);

  const refresh = async () => {
    try {
      const res = await fetch('/auth/me', { credentials: 'same-origin' });
      if (res.status === 200) {
        setUser((await res.json()) as SessionUser);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const logout = () => {
    // Le serveur clear le cookie + redirige vers /login.
    window.location.assign('/auth/logout');
  };

  return (
    <UserContext.Provider value={{ user, refresh, logout }}>{children}</UserContext.Provider>
  );
}

export function useUser(): UserContextValue {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser doit être utilisé dans un <UserProvider>');
  return ctx;
}
